import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faUnlockAlt } from "@fortawesome/free-solid-svg-icons";
import { Col, Row, Form, Card, Button, FormCheck, Container, InputGroup } from "@themesberg/react-bootstrap";
import { Link, useHistory } from "react-router-dom";
import Swal from "sweetalert2";

import { Routes } from "../routes";
import BgImage from "../assets/img/illustrations/signin.svg";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const { login, isAuthenticated } = useAuth();
  const history = useHistory();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // 🔹 Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      history.push(Routes.DashboardOverview.path);
    }
  }, [isAuthenticated, history]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!email || !password) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please enter both email and password.",
        confirmButtonColor: "#3b82f6",
      });
      return;
    }

    setIsLoading(true);

    // Show loading alert
    Swal.fire({
      title: "Signing In...",
      text: "Please wait while we authenticate your credentials.",
      icon: "info",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const success = await login(email, password);
      
      if (success) {
        // Success alert
        await Swal.fire({
          icon: "success",
          title: "Login Successful!",
          text: "Welcome back! Redirecting to dashboard...",
          timer: 1500,
          showConfirmButton: false,
          confirmButtonColor: "#10b981",
        });
        
        // Redirect to dashboard
        history.push(Routes.DashboardOverview.path);
      } else {
        // Error alert for invalid credentials
        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: "Invalid email or password. Please check your credentials and try again.",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      // Network or other errors
      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: "Unable to connect to the server. Please check your internet connection and try again.",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main>
      <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
        <Container>
          <Row
            className="justify-content-center form-bg-image"
            style={{ backgroundImage: `url(${BgImage})` }}
          >
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                <div className="text-center text-md-center mb-4 mt-md-0">
                  <h3 className="mb-0">Welcome Admin</h3>
                </div>

                <Form className="mt-4" onSubmit={handleSubmit}>
                  <Form.Group id="email" className="mb-4">
                    <Form.Label>Your Email</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faEnvelope} />
                      </InputGroup.Text>
                      <Form.Control
                        autoFocus
                        required
                        type="email"
                        placeholder="example@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isLoading}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group id="password" className="mb-4">
                    <Form.Label>Your Password</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faUnlockAlt} />
                      </InputGroup.Text>
                      <Form.Control
                        required
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isLoading}
                      />
                    </InputGroup>
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <Form.Check type="checkbox">
                      <FormCheck.Input id="defaultCheck5" className="me-2" disabled={isLoading} />
                      <FormCheck.Label htmlFor="defaultCheck5" className="mb-0">
                        Remember me
                      </FormCheck.Label>
                    </Form.Check>
                    <Card.Link 
                      className="small text-end" 
                      style={{ 
                        pointerEvents: isLoading ? 'none' : 'auto',
                        opacity: isLoading ? 0.5 : 1
                      }}
                      onClick={() => {
                        if (!isLoading) {
                          Swal.fire({
                            icon: "info",
                            title: "Password Recovery",
                            text: "Please contact your system administrator to reset your password.",
                            confirmButtonColor: "#3b82f6",
                          });
                        }
                      }}
                    >
                      {/* Lost password? */}
                    </Card.Link>
                  </div>

                  <Button 
                    variant="primary" 
                    type="submit" 
                    className="w-100"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing in...
                      </>
                    ) : (
                      "Sign in"
                    )}
                  </Button>
                </Form>

                <div className="d-flex justify-content-center align-items-center mt-5">
                  <span className="fw-normal">
                    {/* Not registered?{" "} */}
                    <Card.Link 
                      as={Link} 
                      to={Routes.Signup.path} 
                      className="fw-bold"
                      style={{ 
                        pointerEvents: isLoading ? 'none' : 'auto',
                        opacity: isLoading ? 0.5 : 1
                      }}
                    >
                      {/* Create account */}
                    </Card.Link>
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default Login;